import { Dexie, EntityTable } from 'dexie'
import OrderJson from 'src/models/order/interfaces/OrderJson'
import { FetchMeta, FetchMetaKey, OrderSummary } from 'src/models/dexie'
import ApplicationEnvironment from 'src/models/application-environment/ApplicationEnvironment'
import ReservationInfoJson from 'src/models/rental/interfaces/ReservationInfoJson'
import ContentBlockJson from 'src/models/Content/interfaces/ContentBlockJson'
import { addFetchMeta, removeFetchMeta } from 'src/services/data/utils'
import { boot } from 'quasar/wrappers'

class DB extends Dexie {
  applicationEnvironment!: EntityTable<
    ApplicationEnvironment & { domain?: string },
    'domain'
  >
  contentBlocks!: EntityTable<ContentBlockJson, 'id'>
  fetchMeta!: EntityTable<FetchMeta, 'key'>
  orderSummaries!: EntityTable<OrderSummary, 'token'>
  orders!: EntityTable<OrderJson, 'token'>
  reservationInfo!: EntityTable<
    ReservationInfoJson & { token: string },
    'token'
  >

  constructor() {
    super('ml-app')
    this.version(1).stores({
      orders: 'token, date',
    })
    this.version(2)
      .stores({
        applicationEnvironment: 'domain',
        contentBlocks: 'id',
        fetchMeta: 'key, fetchedAt',
        orderSummaries: 'token, date',
        orders: 'token',
        reservationInfo: 'token',
      })
      .upgrade(async (trans) => {
        const oldOrders = await trans.table('orders').toArray()
        await trans.table('orderSummaries').bulkAdd(oldOrders)
        await trans.table('orders').clear()
      })
  }
}

const idb = new DB()
export { idb }

function addFetchMetaHooks(table: Dexie.Table, prefix: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  table.hook('creating', (primKey, obj) => {
    addFetchMeta(`${prefix}:${primKey}`)
  })
  table.hook('updating', (modifications, primKey) => {
    addFetchMeta(`${prefix}:${primKey}`)
  })
  table.hook('deleting', (primKey) => {
    removeFetchMeta(`${prefix}:${primKey}`)
  })
}

addFetchMetaHooks(idb.orders, FetchMetaKey.ORDER)
addFetchMetaHooks(idb.reservationInfo, FetchMetaKey.RESERVATION)
addFetchMetaHooks(
  idb.applicationEnvironment,
  FetchMetaKey.APPLICATION_ENVIRONMENT,
)

export default boot(async ({}) => {
  const today = new Date()
  const fourteenDaysAgo = new Date(today)
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)

  const data = idb.fetchMeta.where('fetchedAt').below(fourteenDaysAgo)
  const items = await data.toArray()

  for (const item of items) {
    const [key, token] = item.key.split(':')
    switch (key) {
      case FetchMetaKey.ORDER:
        await idb.transaction('rw', [idb.fetchMeta, idb.orders], async () => {
          await idb.orders.delete(token)
          const remaining = await idb.orders.count()
          if (!remaining) await idb.fetchMeta.delete(FetchMetaKey.ORDERS)
        })
        break
      case FetchMetaKey.RESERVATION:
        await idb.transaction(
          'rw',
          [idb.fetchMeta, idb.reservationInfo],
          async () => {
            await idb.reservationInfo.delete(token)
          },
        )
        break
    }
  }
})
